import logger, { ErrorOptions } from 'shared/services/logger'

export type RetryStrategy =
  | { type: 'exponential'; baseDelay: number; maxDelay?: number }
  | { type: 'fixed'; delay: number }
  | { type: 'linear'; baseDelay: number; maxDelay?: number }
  | { type: 'custom'; calculateDelay: (attempt: number) => number }

export interface RetryOptions<T> {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number

  /** Retry strategy (default: exponential with 250ms base, 5000ms max) */
  strategy?: RetryStrategy

  /** Function to determine if an error should fail immediately (no retries). If false or undefined, error will be retried. */
  shouldFailImmediately?: (error: unknown) => boolean

  /** Optional context for logging (operation name, etc.) */
  context?: {
    operation?: string
    feature?: ErrorOptions['feature']
    logKey?: string
  }

  /** Custom logging function (default: uses logger.debug) */
  onRetry?: (attempt: number, error: unknown, delay: number) => void

  /** Called when all retries are exhausted */
  onExhausted?: (error: unknown, attempts: number) => void

  /** Called on success */
  onSuccess?: (result: T, attempts: number) => void
}

const DEFAULT_STRATEGY: RetryStrategy = {
  type: 'exponential',
  baseDelay: 250,
  maxDelay: 5000,
}

function calculateDelay(attempt: number, strategy: RetryStrategy): number {
  switch (strategy.type) {
    case 'exponential': {
      const delay = strategy.baseDelay * Math.pow(2, attempt - 1)
      return strategy.maxDelay ? Math.min(delay, strategy.maxDelay) : delay
    }
    case 'fixed':
      return strategy.delay
    case 'linear': {
      const delay = strategy.baseDelay * attempt
      return strategy.maxDelay ? Math.min(delay, strategy.maxDelay) : delay
    }
    case 'custom':
      return strategy.calculateDelay(attempt)
  }
}

/**
 * Retries an async function with configurable backoff strategy.
 *
 * @example
 * // Basic usage with defaults (exponential backoff, 3 retries)
 * const result = await retryWithBackoff(() => fetch('/api/data'))
 *
 * @example
 * // Custom retry logic for auth errors
 * const token = await retryWithBackoff(
 *   () => getClientSession(),
 *   {
 *     maxRetries: 3,
 *     strategy: { type: 'exponential', baseDelay: 250, maxDelay: 5000 },
 *     shouldFailImmediately: (error) =>
 *       error instanceof UnauthorizedError || error instanceof ForbiddenError,
 *     context: { operation: 'token-refresh', logKey: 'auth-retry' },
 *   }
 * )
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions<T> = {},
): Promise<T> {
  const {
    maxRetries = 3,
    strategy = DEFAULT_STRATEGY,
    shouldFailImmediately = () => false,
    context,
    onRetry,
    onExhausted,
    onSuccess,
  } = options

  let lastError: unknown
  let attempts = 0

  while (attempts <= maxRetries) {
    try {
      const result = await fn()

      if (attempts > 0 && onSuccess) {
        onSuccess(result, attempts)
      }

      return result
    } catch (error) {
      lastError = error
      attempts++

      // Check if we should fail immediately (e.g., auth errors)
      if (shouldFailImmediately(error)) {
        if (context?.operation) {
          logger.debug(
            `[${context.operation}] Failing immediately: ${error instanceof Error ? error.message : String(error)}`,
          )
        }
        throw error
      }

      // Check if we've exhausted retries
      if (attempts > maxRetries) {
        if (onExhausted) {
          onExhausted(error, attempts)
        } else if (context) {
          logger.error(error, {
            key: context.logKey || 'retry-exhausted',
            feature: context.feature,
            extra: {
              operation: context.operation,
              attempts,
              errorType:
                error instanceof Error ? error.constructor.name : typeof error,
              errorMessage:
                error instanceof Error ? error.message : String(error),
            },
          })
        }
        throw error
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempts, strategy)

      if (onRetry) {
        onRetry(attempts, error, delay)
      } else if (context?.operation) {
        logger.debug(
          `[${context.operation}] Retry attempt ${attempts}/${maxRetries} after ${delay}ms: ${error instanceof Error ? error.message : String(error)}`,
        )
      }

      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  // Should never reach here, but TypeScript needs it
  throw lastError || new Error('Retry exhausted')
}

/**
 * Creates a retry function with pre-configured options.
 * Useful for creating domain-specific retry helpers.
 *
 * @example
 * const retrySessionRefresh = createRetryFunction({
 *   maxRetries: 3,
 *   strategy: { type: 'exponential', baseDelay: 250, maxDelay: 5000 },
 *   shouldFailImmediately: (error) =>
 *     error instanceof UnauthorizedError || error instanceof ForbiddenError,
 *   context: { operation: 'session-refresh', feature: logger.Feature.Auth },
 * })
 *
 * // Later use with defaults:
 * const session = await retrySessionRefresh(() => getClientSession())
 *
 * // Or override options inline for important operations:
 * const session = await retrySessionRefresh(() => getClientSession(), {
 *   maxRetries: 5, // Override default maxRetries for this call
 * })
 */
export function createRetryFunction<T>(
  defaultOptions: RetryOptions<T>,
): <R extends T>(
  fn: () => Promise<R>,
  overrideOptions?: Partial<RetryOptions<T>>,
) => Promise<R> {
  return <R extends T>(
    fn: () => Promise<R>,
    overrideOptions?: Partial<RetryOptions<T>>,
  ) => {
    const options = overrideOptions
      ? { ...defaultOptions, ...overrideOptions }
      : defaultOptions
    return retryWithBackoff(fn, options)
  }
}
